import type { MagiMethod } from "../config";
import type { ModelError, ModelResponse, Phase } from "../types";

const NODE_W = 14; // percent of chamber width
const NODE_H = 20; // percent of chamber height

export function ModelNode({
  id,
  label,
  codename,
  x,
  y,
  phase,
  active,
  response,
  error,
  hasResult,
  method,
  selected,
  onClick,
}: {
  id: string;
  label: string;
  codename: string;
  x: number;
  y: number;
  phase: Phase;
  active: boolean;
  response?: ModelResponse;
  error?: ModelError;
  hasResult: boolean;
  method: MagiMethod;
  selected: boolean;
  onClick: () => void;
}) {
  const processing = phase === "processing" || phase === "dispatch";
  const display = deriveDisplay(method, response, error);
  const clickable = hasResult && (response !== undefined || error !== undefined);

  // The confidence ring is only meaningful when confidence_score is a real
  // probability of the model's own certainty (Vote / free-text methods). For
  // Probability the score *is* the answer, so the ring would double-count the
  // value already shown as primary.
  const ringValue = ringValueFor(method, response);

  return (
    <div
      className={
        `node${active ? " active" : ""}${selected ? " selected" : ""}` +
        `${phase === "dispatch" ? " dispatched" : ""}` +
        `${clickable ? " clickable" : ""}` +
        `${error ? " has-error" : ""}`
      }
      style={{
        left: `${x}%`,
        top: `${y}%`,
      }}
      role="button"
      tabIndex={0}
      aria-label={
        clickable
          ? `Inspect ${label} — click for full response`
          : `Model ${label}`
      }
      title={clickable ? "Click to inspect this model's reasoning" : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      data-testid={`node-${id}`}
    >
      {ringValue !== null && (
        <ConfidenceRing value={ringValue} color={display.primaryColor} />
      )}
      <div className="shell">
        <span className="codename">{codename}</span>
        <span className="model-name" title={id}>
          {label}
        </span>
        {processing ? (
          <span
            className="processing-dots"
            style={{ color: "var(--accent)", fontSize: 12, fontWeight: 600, letterSpacing: "0.14em" }}
          >
            PROCESSING
          </span>
        ) : error ? (
          <>
            <span
              className="value"
              style={{ color: "var(--negative)", fontSize: 14 }}
            >
              FAILED
            </span>
            <span
              className="confidence"
              style={{ color: "var(--text-dim)" }}
              title={error.error}
            >
              {truncate(error.error_category ?? "see drawer", 16)}
            </span>
          </>
        ) : hasResult && response ? (
          <>
            <span
              className="value"
              style={{ color: display.primaryColor, fontSize: display.primarySize ?? 20 }}
            >
              {display.primary}
            </span>
            {display.secondary && (
              <span className="confidence" style={display.secondaryColor ? { color: display.secondaryColor } : undefined}>
                {display.secondary}
              </span>
            )}
          </>
        ) : hasResult ? (
          <span className="value" style={{ color: "var(--text-dim)", fontSize: 14 }}>
            —
          </span>
        ) : (
          <span
            style={{
              color: "var(--text-mute)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.14em",
            }}
          >
            STANDBY
          </span>
        )}
        {clickable && (
          <span className="node-click-hint" aria-hidden>
            ↗
          </span>
        )}
      </div>
    </div>
  );
}

function ConfidenceRing({ value, color }: { value: number; color: string }) {
  const r = 44;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - value);
  return (
    <svg className="confidence-ring" viewBox="-50 -50 100 100" aria-hidden>
      <circle
        r={r}
        className="ring-arc"
        stroke="var(--border)"
      />
      <circle
        r={r}
        className="ring-arc"
        stroke={color}
        strokeDasharray={c}
        strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 0.9s ease" }}
      />
    </svg>
  );
}

type Display = {
  primary: string;
  primaryColor: string;
  primarySize?: number;
  secondary?: string;
  secondaryColor?: string;
};

/**
 * Per-method summary of what to show inside the node tile. The historical
 * implementation double-counted `confidence_score` for Probability (where the
 * score IS the probability) and labelled non-abstained free-text responses as
 * "abstain" whenever the model returned -1. This helper makes the mapping
 * explicit and method-specific so the tile never shows a misleading label.
 */
function deriveDisplay(
  method: MagiMethod,
  resp?: ModelResponse,
  err?: ModelError,
): Display {
  if (err) {
    return { primary: "FAILED", primaryColor: "var(--negative)", primarySize: 16 };
  }
  if (!resp) {
    return { primary: "—", primaryColor: "var(--text-dim)" };
  }
  const raw = resp.response;
  const c = resp.confidence_score;

  if (method === "VoteYesNo") {
    const v = String(raw ?? "").toLowerCase();
    if (v === "yes") return withConf({ primary: "YES", primaryColor: "var(--positive)" }, c);
    if (v === "no") return withConf({ primary: "NO", primaryColor: "var(--negative)" }, c);
    return { primary: "ABSTAIN", primaryColor: "var(--accent)" };
  }

  if (method === "VoteOptions") {
    const s = String(raw ?? "").toUpperCase();
    return withConf(
      { primary: truncate(s, 12), primaryColor: "var(--accent)", primarySize: 16 },
      c,
    );
  }

  if (method === "Probability") {
    // For Probability the confidence_score IS the estimate; -1 means abstain.
    // Do NOT show a separate "confidence" line — it would double-count.
    if (!Number.isFinite(c) || c < 0) {
      return { primary: "ABSTAIN", primaryColor: "var(--accent)", secondary: "no estimate" };
    }
    const pct = Math.round(Math.max(0, Math.min(1, c)) * 100);
    return {
      primary: `${pct}%`,
      primaryColor: c >= 0.5 ? "var(--positive)" : "var(--negative)",
      secondary: "probability",
      secondaryColor: "var(--text-dim)",
    };
  }

  // Majority / Consensus / Minority / Synthesis / Compose — the raw response
  // is free text (often a full paragraph). A 14-char chip would be meaningless,
  // so we surface the model's confidence as the primary value and invite the
  // user into the drawer for the full text.
  if (!Number.isFinite(c) || c < 0) {
    return {
      primary: "—",
      primaryColor: "var(--text-dim)",
      secondary: "no confidence",
    };
  }
  return {
    primary: `${Math.round(Math.max(0, Math.min(1, c)) * 100)}%`,
    primaryColor: "var(--accent)",
    secondary: "confidence",
    secondaryColor: "var(--text-dim)",
  };
}

function withConf(base: Omit<Display, "secondary">, c: number): Display {
  if (!Number.isFinite(c) || c < 0) {
    return { ...base, secondary: "no confidence", secondaryColor: "var(--text-dim)" };
  }
  return {
    ...base,
    secondary: `${Math.round(Math.max(0, Math.min(1, c)) * 100)}% confident`,
    secondaryColor: "var(--text-dim)",
  };
}

function ringValueFor(method: MagiMethod, resp?: ModelResponse): number | null {
  if (!resp) return null;
  const c = resp.confidence_score;
  if (!Number.isFinite(c) || c < 0) return null;
  // For Probability the primary number already expresses the estimate — drawing
  // a ring of the same length would just duplicate the reading.
  if (method === "Probability") return null;
  return Math.max(0, Math.min(1, c));
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

export const _testing = { deriveDisplay, ringValueFor, NODE_W, NODE_H };
